import { zodResolver } from '@hookform/resolvers/zod'
import Rating from '@repo/ui/rating'
import Typography from '@repo/ui/typography'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import useTranslate from '../../../lib/hooks/use-translate'
import { MissionToStaffReportInput } from '../../../lib/types/models/mission-to-staff/mission-to-staff.input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { createTimeSchema } from '../../../lib/utils/validations/date.validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import Form, { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledHourInput from '../../elements/inputs/controlled-hour-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ValidationButtons from '../../elements/validation-buttons'

const MissionToStaffReportForm = ({
  defaultValues,
  ...validationButtonProps
}: BaseFormProps<MissionToStaffReportInput>) => {
  const { t } = useTranslate()

  const formSchema: ZodType<MissionToStaffReportInput> = z.object({
    startTime: createTimeSchema({
      inputName: t('staff.personal.space.missions.report.start.time')
    }),
    endTime: createTimeSchema({ inputName: t('staff.personal.space.missions.report.end.time') }),
    content: createStringSchema(t('staff.personal.space.missions.report.content.error'), {
      errorMessage: t('staff.personal.space.missions.report.content.error')
    }),
    comments: createStringSchema(t('staff.personal.space.missions.report.comments.placeholder'), {
      errorMessage: t('staff.personal.space.missions.report.comments.placeholder'),
      nullish: true
    }),
    rating: createNumberSchema("Nombre d'étoiles", {
      minimumValue: 0,
      maximumValue: 5,
      errorMessage: "Veuillez saisir un nombre d'étoiles entre 0 et 5"
    }).default(0)
  })

  const form = useForm<MissionToStaffReportInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form form={form}>
      <form
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-row gap-4 items-center">
          <FormField
            control={form.control}
            name="startTime"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledHourInput
                    label={t('staff.personal.space.missions.report.start.time')}
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="endTime"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledHourInput
                    label={t('staff.personal.space.missions.report.end.time')}
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Typography variant="tag">
            {t('staff.personal.space.missions.report.content.title')}
          </Typography>

          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="textarea"
                    placeholder={t('staff.personal.space.missions.report.content.placeholder')}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Typography variant="tag">
            {t('staff.personal.space.missions.report.comments.title')}
          </Typography>
          <Rating
            mode="edit"
            defaultValue={form.watch('rating') || 0}
            onChange={(value) => form.setValue('rating', value)}
          />
          <FormField
            control={form.control}
            name="comments"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="textarea"
                    placeholder={t('staff.personal.space.missions.report.comments.placeholder')}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default MissionToStaffReportForm
